/**
 * FlickityNative — Drop-in vanilla JS replacement for Flickity v2
 *
 * Usage:
 *   var flkty = new FlickityNative('.carousel', {
 *     wrapAround: true, freeScroll: true, pageDots: true,
 *     contain: true, draggable: true, prevNextButtons: true
 *   });
 *   flkty.previous();  flkty.next();  flkty.select(2);
 *
 * Also accepts data-flickity='{ ... }' attribute (JSON) for declarative init.
 */
(function () {
  'use strict';

  function FlickityNative(selector, options) {
    var el = typeof selector === 'string' ? document.querySelector(selector) : selector;
    if (!el) return;

    // check for data-flickity attribute
    if (!options) {
      var attr = el.getAttribute('data-flickity');
      if (attr) {
        try { options = JSON.parse(attr); } catch (e) { options = {}; }
      } else {
        options = {};
      }
    }

    var defaults = {
      cellAlign: 'center',
      wrapAround: false,
      freeScroll: false,
      imagesLoaded: false,
      pageDots: true,
      contain: true,
      draggable: true,
      prevNextButtons: true,
      groupCells: false,
      selectedAttraction: 0.025,
      friction: 0.28,
      freeScrollFriction: 0.075
    };

    var o = {};
    for (var k in defaults) o[k] = defaults[k];
    for (var k2 in options) o[k2] = options[k2];

    // ── state ──
    var viewport, slider;
    var cells = [];
    var selectedIndex = 0;
    var isAnimating = false;
    var animTimer = null;
    var translateX = 0;

    // drag state
    var isDragging = false;
    var dragStartX = 0;
    var dragStartY = 0;
    // 'h' = carousel owns the gesture, 'v' = handed off to native page scroll,
    // null = intent not yet known (first few px of a touch).
    var dragAxis = null;
    var dragCurrentX = 0;
    var dragStartTranslate = 0;
    var velocity = 0;
    var lastDragX = 0;
    var lastDragTime = 0;
    var rafId = null;

    // ── build ──
    function build() {
      el.classList.add('flickity-enabled');
      // Inline pan-y on the carousel root, not just the viewport. touch-action
      // resolves as the intersection of the whole ancestor chain, so a single
      // stylesheet rule putting pan-x/none on any wrapper silently kills native
      // vertical page scrolling for every touch that starts in the carousel
      // (this shipped once via a block's {% style %} and froze the page on iOS).
      // Inline style outranks any stylesheet, old or new, so the drag stays
      // JS-owned and vertical scroll stays native no matter what CSS ships.
      el.style.touchAction = 'pan-y';

      var children = Array.prototype.slice.call(el.children);

      viewport = document.createElement('div');
      viewport.className = 'flickity-viewport';
      viewport.style.position = 'relative';
      viewport.style.overflow = 'hidden';
      viewport.style.touchAction = 'pan-y';

      slider = document.createElement('div');
      slider.className = 'flickity-slider';
      slider.style.position = 'relative';
      slider.style.display = 'flex';
      slider.style.willChange = 'transform';

      children.forEach(function (child) {
        child.classList.add('flickity-cell');
        slider.appendChild(child);
        cells.push(child);
      });

      viewport.appendChild(slider);
      el.innerHTML = '';
      el.appendChild(viewport);

      if (o.prevNextButtons) buildButtons();
      if (o.pageDots) buildDots();

      updateSelectedClass();
      bindEvents();

      if (o.imagesLoaded) {
        var imgs = el.querySelectorAll('img');
        var loaded = 0;
        var total = imgs.length;
        if (total === 0) return;
        imgs.forEach(function (img) {
          if (img.complete) {
            loaded++;
            if (loaded === total) layout();
          } else {
            img.addEventListener('load', function () {
              loaded++;
              if (loaded === total) layout();
            });
          }
        });
      }
    }

    function buildButtons() {
      var prev = document.createElement('button');
      prev.type = 'button';
      prev.className = 'flickity-button flickity-prev-next-button previous';
      prev.setAttribute('aria-label', 'Previous');
      prev.innerHTML = '<svg viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 70,90 L 30,50  L 70,10 L 60,0 Z" class="arrow"></path></svg>';
      prev.addEventListener('click', function (e) { e.preventDefault(); previous(); });

      var next = document.createElement('button');
      next.type = 'button';
      next.className = 'flickity-button flickity-prev-next-button next';
      next.setAttribute('aria-label', 'Next');
      next.innerHTML = '<svg viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 70,90 L 30,50  L 70,10 L 60,0 Z" class="arrow" transform="translate(100, 100) rotate(180)"></path></svg>';
      next.addEventListener('click', function (e) { e.preventDefault(); nextSlide(); });

      el.appendChild(prev);
      el.appendChild(next);
    }

    var dotsContainer;
    function buildDots() {
      dotsContainer = document.createElement('ol');
      dotsContainer.className = 'flickity-page-dots';
      el.appendChild(dotsContainer);
      renderDots();
    }

    function renderDots() {
      if (!dotsContainer) return;
      dotsContainer.innerHTML = '';
      for (var i = 0; i < cells.length; i++) {
        var dot = document.createElement('li');
        dot.className = 'dot' + (i === selectedIndex ? ' is-selected' : '');
        (function (idx) {
          dot.addEventListener('click', function () { select(idx); });
        })(i);
        dotsContainer.appendChild(dot);
      }
    }

    // ── layout ──
    function layout() {
      // nothing to recalculate — cells use their natural / CSS-set widths
    }

    function getCellPositions() {
      var positions = [];
      var x = 0;
      for (var i = 0; i < cells.length; i++) {
        var cell = cells[i];
        var w = cell.offsetWidth;
        var mr = parseInt(window.getComputedStyle(cell).marginRight) || 0;
        positions.push({ left: x, width: w, center: x + w / 2 });
        x += w + mr;
      }
      return positions;
    }

    function getTargetX(idx) {
      var positions = getCellPositions();
      if (!positions[idx]) return 0;
      var viewW = viewport.offsetWidth;

      if (o.cellAlign === 'center') {
        return -(positions[idx].center - viewW / 2);
      } else {
        return -positions[idx].left;
      }
    }

    function setTranslate(x, animate) {
      translateX = x;
      if (animate) {
        slider.style.transition = 'transform 0.4s ease';
      } else {
        slider.style.transition = 'none';
      }
      slider.style.transform = 'translate3d(' + x + 'px, 0, 0)';
    }

    function updateSelectedClass() {
      cells.forEach(function (c, i) {
        c.classList.toggle('is-selected', i === selectedIndex);
      });
      renderDots();
    }

    // ── navigation ──
    function select(idx, isWrapping) {
      if (isAnimating && !isWrapping) return;

      if (o.wrapAround) {
        if (idx < 0) idx = cells.length - 1;
        if (idx >= cells.length) idx = 0;
      } else {
        idx = Math.max(0, Math.min(idx, cells.length - 1));
      }

      selectedIndex = idx;

      var target = getTargetX(idx);

      // contain: prevent scrolling past the end
      if (o.contain && !o.wrapAround) {
        var positions = getCellPositions();
        var lastPos = positions[positions.length - 1];
        var maxScroll = -(lastPos.left + lastPos.width - viewport.offsetWidth);
        if (maxScroll > 0) maxScroll = 0;
        if (target < maxScroll) target = maxScroll;
        if (target > 0) target = 0;
      }

      // If already at the target position, skip animation entirely.
      // Without this guard, setTranslate would apply a CSS transition but the
      // transform value wouldn't change, so transitionend would never fire —
      // leaving isAnimating=true permanently and blocking all future select() calls.
      if (target === translateX) {
        updateSelectedClass();
        return;
      }

      isAnimating = true;
      setTranslate(target, true);

      slider.addEventListener('transitionend', function handler() {
        slider.removeEventListener('transitionend', handler);
        clearTimeout(animTimer);
        isAnimating = false;
      });
      // transitionend is unreliable: it never fires if the transition is
      // interrupted (a touchstart sets transition:'none', the element is
      // hidden, Safari drops it, ...) and a missed one left isAnimating=true
      // forever — select() then no-oped and the slider stayed wherever the
      // last drag put it. Always clear shortly after the 0.4s duration.
      clearTimeout(animTimer);
      animTimer = setTimeout(function () { isAnimating = false; }, 450);

      updateSelectedClass();
    }

    function nextSlide() {
      select(selectedIndex + 1);
    }

    function previous() {
      select(selectedIndex - 1);
    }

    // ── drag ──
    // Reset drag state and snap the slider back to its selected slide.
    // Used when the gesture is handed to native scrolling (vertical axis) and
    // when the browser cancels the touch (touchcancel) — without the snap the
    // slider stays stranded at whatever mid-drag transform was last applied.
    function cancelDrag() {
      if (!isDragging) return;
      isDragging = false;
      setTranslate(dragStartTranslate, false);
    }

    function onPointerDown(e) {
      if (!o.draggable) return;
      if (e.button && e.button !== 0) return; // left click only
      isDragging = true;
      // The drag owns the slider now. Setting transition:'none' below kills any
      // in-flight select() animation, so its transitionend will never fire —
      // release the animation lock here or select() stays blocked forever.
      isAnimating = false;
      var isTouch = e.type === 'touchstart';
      dragStartX = isTouch ? e.touches[0].clientX : e.clientX;
      dragStartY = isTouch ? e.touches[0].clientY : e.clientY;
      // Mouse drags can't scroll the page, so only touch needs axis detection.
      dragAxis = isTouch ? null : 'h';
      dragCurrentX = dragStartX;
      dragStartTranslate = translateX;
      lastDragX = dragStartX;
      lastDragTime = Date.now();
      velocity = 0;
      slider.style.transition = 'none';
    }

    function onPointerMove(e) {
      if (!isDragging) return;
      var isTouch = e.type === 'touchmove';
      var x = isTouch ? e.touches[0].clientX : e.clientX;

      // Commit the touch gesture to one axis on the FIRST touchmove — no
      // "wait for intent" distance. iOS Safari applies its own movement slop
      // before firing touchmove, and if the first touchmove goes unprevented
      // Safari may claim the gesture as a native scroll and fire touchcancel,
      // leaving the slider stranded mid-drag. Deciding immediately means a
      // horizontal swipe preventDefaults the first (still cancelable) event,
      // which is the only reliable way to keep WebKit from stealing it.
      // Vertical → abandon the drag so native pan-y page scrolling owns it.
      if (dragAxis === null) {
        var adx = Math.abs(x - dragStartX);
        var ady = Math.abs(e.touches[0].clientY - dragStartY);
        // Ties (incl. 0/0) count as vertical: page scroll must win when unsure.
        dragAxis = adx > ady ? 'h' : 'v';
        if (dragAxis === 'v') {
          cancelDrag();
          return;
        }
      }
      if (isTouch && e.cancelable) e.preventDefault();
      dragCurrentX = x;

      var now = Date.now();
      var dt = now - lastDragTime;
      if (dt > 0) {
        velocity = (x - lastDragX) / dt;
      }
      lastDragX = x;
      lastDragTime = now;

      var diff = dragCurrentX - dragStartX;
      slider.style.transform = 'translate3d(' + (dragStartTranslate + diff) + 'px, 0, 0)';
      translateX = dragStartTranslate + diff;
    }

    function onPointerUp() {
      if (!isDragging) return;
      isDragging = false;

      var diff = dragCurrentX - dragStartX;

      if (o.freeScroll) {
        // momentum scroll
        var target = translateX + velocity * 200;

        if (o.contain) {
          var positions = getCellPositions();
          var lastPos = positions[positions.length - 1];
          var maxScroll = -(lastPos.left + lastPos.width - viewport.offsetWidth);
          if (maxScroll > 0) maxScroll = 0;
          if (target < maxScroll) target = maxScroll;
          if (target > 0) target = 0;
        }

        setTranslate(target, true);

        // find closest cell
        var positions2 = getCellPositions();
        var closestIdx = 0;
        var closestDist = Infinity;
        for (var i = 0; i < positions2.length; i++) {
          var cellCenter = positions2[i].center;
          var viewCenter = -target + viewport.offsetWidth / 2;
          var dist = Math.abs(cellCenter - viewCenter);
          if (dist < closestDist) {
            closestDist = dist;
            closestIdx = i;
          }
        }
        selectedIndex = closestIdx;
        updateSelectedClass();
      } else {
        var threshold = viewport.offsetWidth * 0.15;
        if (diff < -threshold) {
          nextSlide();
        } else if (diff > threshold) {
          previous();
        } else {
          select(selectedIndex);
        }
      }
    }

    // ── events ──
    function bindEvents() {
      viewport.addEventListener('touchstart', onPointerDown, { passive: true });
      // passive:false so a horizontal-committed swipe can preventDefault and
      // keep the page still; vertical swipes bail out before ever calling it.
      viewport.addEventListener('touchmove', onPointerMove, { passive: false });
      viewport.addEventListener('touchend', onPointerUp);
      // iOS Safari fires touchcancel (never touchend) when the system claims
      // the gesture — native scroll takeover, edge swipe, notification, etc.
      // Without this, isDragging stays true and the slider freezes mid-drag,
      // and the stale drag preventDefaults later touchmoves, blocking page
      // scroll on the carousel. Chrome rarely cancels, which is why the bug
      // showed up as Safari-only.
      viewport.addEventListener('touchcancel', cancelDrag);

      viewport.addEventListener('mousedown', onPointerDown);
      document.addEventListener('mousemove', onPointerMove);
      document.addEventListener('mouseup', onPointerUp);

      viewport.addEventListener('dragstart', function (e) { e.preventDefault(); });

      // click prevention during drag
      viewport.addEventListener('click', function (e) {
        if (Math.abs(dragCurrentX - dragStartX) > 5) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, true);

      var resizeTimer;
      window.addEventListener('resize', function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function () {
          select(selectedIndex);
        }, 200);
      });
    }

    // ── public API ──
    this.select = select;
    this.next = nextSlide;
    this.previous = previous;
    this.selectedIndex = function () { return selectedIndex; };
    this.cells = cells;

    this.resize = function () {
      // Reset isAnimating in case a CSS transition was interrupted by the
      // gallery being hidden (display:none). When that happens, transitionend
      // never fires, leaving isAnimating=true and blocking all future select()
      // calls. Resetting here and repositioning instantly (animate=false) lets
      // the slider recover cleanly every time the gallery becomes visible again.
      isAnimating = false;
      var target = getTargetX(selectedIndex);
      setTranslate(target, false);
      updateSelectedClass();
    };

    this.destroy = function () {
      if (rafId) cancelAnimationFrame(rafId);
      el.innerHTML = '';
      cells.forEach(function (c) {
        c.classList.remove('flickity-cell', 'is-selected');
        el.appendChild(c);
      });
      el.classList.remove('flickity-enabled');
      el._flkty = null;
      el.flickity = null;
    };

    // init
    build();

    // Store instance on element for external retrieval (mirrors real Flickity API)
    el._flkty = this;
    el.flickity = this;
  }

  // static .data(element) — returns existing instance attached to element (Flickity v2 compat)
  FlickityNative.data = function (el) {
    if (typeof el === 'string') el = document.querySelector(el);
    if (!el) return null;
    return el._flickityNative || el.flickity || null;
  };

  // expose globally
  window.FlickityNative = FlickityNative;

  // shim for `new Flickity(...)` calls
  window.Flickity = window.Flickity || FlickityNative;

  // auto-init elements with data-flickity attribute
  document.addEventListener('DOMContentLoaded', function () {
    var els = document.querySelectorAll('[data-flickity]');
    els.forEach(function (el) {
      if (el._flickityNative) return;
      el._flickityNative = new FlickityNative(el);
    });
  });
})();
